import React, {
  ChangeEvent, ReactElement, useEffect, useState,
} from 'react';
import {
  Table, Tag, Tbody, Td, Text, Th, Thead, Tr,
} from 'idsk-react-components';
import { useResponsiveContext } from 'slovlex-idsk-react-components';
import SprievodnyDokumentMergedItem from '../../../../model/pracovnyKrok/SprievodnyDokumentMergedItem';
import Actions from './Actions';
import Uuid from '../../../../model/Uuid';
import { useClient } from '../../../../context/ClientStatusContext';
import { createSprievodnyDokument } from '../../../../client/pracovnyKrok/sprievodnyDokument/sprievodnyDokumentClient';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import {
  deleteSprievodnyDokument,
} from '../../../../client/pracovnyKrok/sprievodnyDokument/sprievodnyDokumentDeleteClient';
import downloadFile from '../../../../function/downloadFile';
import {
  downloadSprievodnyDokumentByUuid,
} from '../../../../client/external/evidencnaaplikacia/sprievodnyDokumentDownloadClient';
import { useUserContext } from '../../../../context/UserContext';
import { UserRoleEnum } from '../../../../model/currentuser/UserRole';
import LegislativnyMaterialDetail from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import StadiumMaterialuHistoria from '../../../../model/legislativnyMaterial/StadiumMaterialuHistoria';
import scrollToTop from '../../../../function/scrollToTop';
import formatDatumUlozenia from '../../../../function/formatDatumUlozenia';
import SprievodnyDokumentDetailRow from './SprievodnyDokumentDetailRow';
import getFileSize from '../../../../function/getFileSize';

interface Props {
  successMessageToDisplay: string;
  setSuccessMessageToDisplay: (message: string) => void;
  selectedStadium: StadiumMaterialuHistoria,
  sprievodnyDokumentMergedList: SprievodnyDokumentMergedItem[];
  reloadSprievodnyDokumentList: () => void;
  materialDetail: LegislativnyMaterialDetail;
  stadiumUuid: Uuid;
}

const SprievodnyDokumentUpdateTable = ({
  successMessageToDisplay,
  setSuccessMessageToDisplay,
  selectedStadium,
  sprievodnyDokumentMergedList,
  reloadSprievodnyDokumentList,
  materialDetail,
  stadiumUuid,
}: Props): ReactElement => {
  const { displaySuccess } = useSuccessMessageContext();
  const { pouzivatel } = useUserContext();
  const isSuperAdmin = pouzivatel.roles?.includes(UserRoleEnum.SUPERADMIN);
  const [uploadingDokument, setUploadingDokument] = useState<SprievodnyDokumentMergedItem>();
  const [downloadingDokumentUuid, setDownloadingDokumentUuid] = useState<string>();
  const [deletingDokumentUuid, setDeletingDokumentUuid] = useState<string>();

  function isVisibleDocument(document: SprievodnyDokumentMergedItem): boolean {
    const documentExists = !!document?.sprievodnyDokumentUuid;
    const isGenerovany = document?.typDokumentu?.generovanySystemom === true;

    return isSuperAdmin || documentExists || !isGenerovany;
  }

  const {
    wrapClient: createSprievodnyDokumentClient,
    status: createSprievodnyDokumentStatus,
  } = useClient(createSprievodnyDokument);
  const uploadDokument = async (e: ChangeEvent<HTMLInputElement>, dokument?: SprievodnyDokumentMergedItem)
    : Promise<void> => {
    if (e.target.files.length > 0) {
      setUploadingDokument(dokument);
      const sprievodnyDokumentRequest = {
        legMaterialUuid: materialDetail.uuid,
        typDokumentu: dokument.typDokumentu.uuid,
        sprievodnyDokument: e.target.files[0],
      };
      await createSprievodnyDokumentClient(sprievodnyDokumentRequest, stadiumUuid);
      setUploadingDokument(undefined);
      setSuccessMessageToDisplay('Sprievodný dokument bol úspešne pridaný.');
      reloadSprievodnyDokumentList();
    }
  };

  useEffect(() => {
    if (successMessageToDisplay !== '') {
      displaySuccess(successMessageToDisplay);
      scrollToTop();
      setSuccessMessageToDisplay('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprievodnyDokumentMergedList]);

  const {
    wrapClient: deleteSprievodnyDokumentClient,
    status: deleteSprievodnyDokumentStatus,
  } = useClient(deleteSprievodnyDokument);
  const deleteDokument = async (dokumentUuid: Uuid): Promise<void> => {
    setDeletingDokumentUuid(dokumentUuid);
    await deleteSprievodnyDokumentClient(materialDetail.uuid, dokumentUuid);
    setDeletingDokumentUuid(undefined);
    setSuccessMessageToDisplay('Dokument bol úspešne odstránený.');
    reloadSprievodnyDokumentList();
  };

  const {
    wrapClient: downloadSprievodnyDokumentClient,
    status: downloadSprievodnyDokumentStatus,
  } = useClient(downloadSprievodnyDokumentByUuid);
  const downloadDokument = async (dokumentUuid: Uuid, filename: string): Promise<void> => {
    setDownloadingDokumentUuid(dokumentUuid);
    const { data } = await downloadSprievodnyDokumentClient(dokumentUuid);
    setDownloadingDokumentUuid(undefined);
    if (data) {
      downloadFile(filename, data);
    }
  };

  const { isSmallScreen } = useResponsiveContext();

  if (sprievodnyDokumentMergedList.length === 0) {
    return <Text type="p" size="l" className="text-center">Nenašli sa žiadne záznamy.</Text>;
  }

  if (isSmallScreen) {
    return (
      <div>
        {sprievodnyDokumentMergedList?.filter(dokument => isVisibleDocument(dokument))
          .map((dokument, index) => (
            <SprievodnyDokumentDetailRow
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              nazov={dokument.nazov}
              typDokumentu={dokument.typDokumentu?.nazov}
              velkost={dokument.velkost}
              datumUlozenia={dokument.datumUlozenia}
              povinne={dokument.sablonaUuid && dokument.povinny}
              actions={(
                <Actions
                  setSuccessMessageToDisplay={setSuccessMessageToDisplay}
                  selectedStadium={selectedStadium}
                  sprievodnyDokument={dokument}
                  uploadDokument={uploadDokument}
                  uploadDokumentStatus={uploadingDokument === dokument ? createSprievodnyDokumentStatus : undefined}
                  deleteDokument={deleteDokument}
                  deleteDokumentStatus={deletingDokumentUuid === dokument.sprievodnyDokumentUuid
                    ? deleteSprievodnyDokumentStatus
                    : undefined}
                  downloadDokument={downloadDokument}
                  downloadDokumentStatus={downloadingDokumentUuid === dokument.sprievodnyDokumentUuid
                    ? downloadSprievodnyDokumentStatus
                    : undefined}
                  materialDetail={materialDetail}
                  reloadSprievodnyDokumentList={reloadSprievodnyDokumentList}
                  useTooltipIcons
                />
)}
            />
          ))}
      </div>
    );
  }

  return (
    <div className="sprievodny-dokument-list__table">
      <Table>
        <Thead>
          <Tr>
            <Th width="35%">
              Názov
            </Th>
            <Th width="20%">
              Typ dokumentu
            </Th>
            <Th width="10%">Veľkosť</Th>
            <Th width="20%">Dátum a čas uloženia</Th>
            <Th width="15%" />
          </Tr>
        </Thead>
        <Tbody>
          {sprievodnyDokumentMergedList?.filter(dokument => isVisibleDocument(dokument))
            .map((dokument, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={index}>
                <Td>{dokument.nazov}</Td>
                <Td>{dokument.sablonaUuid && dokument.povinny && (
                  <>
                    <Tag size="medium">
                      Povinné
                    </Tag><br />
                  </>
                )}{dokument.typDokumentu?.nazov}
                </Td>
                <Td>
                  {(dokument.velkost || dokument.velkost === 0) && `${getFileSize(dokument.velkost)}`}
                </Td>
                <Td>
                  {dokument.datumUlozenia && formatDatumUlozenia(dokument.datumUlozenia)}
                </Td>
                <Td>
                  <Actions
                    setSuccessMessageToDisplay={setSuccessMessageToDisplay}
                    selectedStadium={selectedStadium}
                    sprievodnyDokument={dokument}
                    uploadDokument={uploadDokument}
                    uploadDokumentStatus={uploadingDokument === dokument ? createSprievodnyDokumentStatus : undefined}
                    deleteDokument={deleteDokument}
                    deleteDokumentStatus={deletingDokumentUuid === dokument.sprievodnyDokumentUuid
                      ? deleteSprievodnyDokumentStatus
                      : undefined}
                    downloadDokument={downloadDokument}
                    downloadDokumentStatus={downloadingDokumentUuid === dokument.sprievodnyDokumentUuid
                      ? downloadSprievodnyDokumentStatus
                      : undefined}
                    materialDetail={materialDetail}
                    reloadSprievodnyDokumentList={reloadSprievodnyDokumentList}
                    useTooltipIcons
                  />
                </Td>
              </Tr>
            ))}
        </Tbody>
      </Table>
    </div>
  );
};

export default SprievodnyDokumentUpdateTable;
